import React, { useCallback, useState } from 'react';

const Step1_FileUpload = ({ onNext, loading, error, setError }) => {
  const [file, setFile] = useState(null);
  const [fileContent, setFileContent] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleFileChange = useCallback((e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if (selectedFile.type !== 'application/json' && !selectedFile.name.endsWith('.json')) {
      setError('Please upload a JSON file');
      return;
    }

    setFile(selectedFile);
    setError('');
    setIsProcessing(true);

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const json = JSON.parse(event.target.result);

        // Extract only clinic_doctors and clinic_doctor_schedules
        const extractedData = {
          clinic_doctors: json.clinic_doctors || [],
          clinic_doctor_schedules: json.clinic_doctor_schedules || []
        };

        if (extractedData.clinic_doctors.length === 0 && extractedData.clinic_doctor_schedules.length === 0) {
          setError('No clinic_doctors or clinic_doctor_schedules found in the JSON file');
          setFile(null);
          setFileContent(null);
        } else {
          setFileContent(extractedData);
        }
      } catch (err) {
        setError('Invalid JSON file. Please check the file format.');
        setFile(null);
        setFileContent(null);
      } finally {
        setIsProcessing(false);
      }
    };

    reader.onerror = () => {
      setError('Error reading file');
      setFile(null);
      setFileContent(null);
      setIsProcessing(false);
    };

    reader.readAsText(selectedFile);
  }, [setError]);

  const handleRemoveFile = useCallback(() => {
    setFile(null);
    setFileContent(null);
    setError('');
  }, []);

  const handleNext = useCallback(() => {
    if (!fileContent) {
      setError('Please upload a valid JSON file');
      return;
    }
    onNext(fileContent);
  }, [fileContent, onNext, setError]);

  const doctorsCount = fileContent?.clinic_doctors?.length || 0;
  const schedulesCount = fileContent?.clinic_doctor_schedules?.length || 0;

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Step 1: Upload JSON File</h2>
        <p className="text-gray-600">Upload a JSON file containing clinic_doctors and clinic_doctor_schedules. The JSON can be exported from Clinic Config > Export > Export Clinic Config.</p>
      </div>

      {/* File Upload Section */}
      <div className="bg-gray-50 p-6 rounded-lg">
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select JSON File
          </label>
          <div className="flex items-center gap-4">
            <label className="flex-1 cursor-pointer">
              <input
                type="file"
                accept=".json,application/json"
                onChange={handleFileChange}
                className="hidden"
                disabled={isProcessing || loading}
              />
              <div className="flex items-center justify-center px-6 py-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors">
                <div className="text-center">
                  <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                    <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <p className="mt-2 text-sm text-gray-600">
                    {isProcessing ? 'Processing...' : 'Click to upload or drag and drop'}
                  </p>
                  <p className="text-xs text-gray-500">JSON files only</p>
                </div>
              </div>
            </label>
          </div>
        </div>

        {file && fileContent && (
          <div className="mt-4 space-y-2">
            <div className="flex items-center justify-between p-4 bg-white border border-green-200 rounded-lg">
              <div className="flex items-center gap-3">
                <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className="text-sm font-medium text-gray-900">{file.name}</p>
                  <p className="text-xs text-gray-500">
                    {doctorsCount} doctor{doctorsCount !== 1 ? 's' : ''}, {schedulesCount} schedule{schedulesCount !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleRemoveFile}
                className="text-red-600 hover:text-red-700"
                aria-label="Remove file"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="text-red-800">{error}</div>
        </div>
      )}

      {/* Next Button */}
      <div className="flex justify-end">
        <button
          onClick={handleNext}
          disabled={!fileContent || isProcessing || loading}
          className="px-8 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
        >
          {loading ? 'Loading...' : 'Next Step →'}
        </button>
      </div>
    </div>
  );
};

export default Step1_FileUpload;
